import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, map, shareReplay, switchMap } from "rxjs";
import { Effort } from "../models/effort";
import { environment } from "../../environments/environment";
import { BaseService } from "./base.service";
import { FilterChip } from "../models/filter-chip";
import { Project } from "../models/project";
import { Group, GroupEfforts as GroupEffort } from "../models/group";

@Injectable({ providedIn: 'root' })
export class EffortsService extends BaseService {
    private sortProperties: { [key: string]: string } = {
        "key": "Key", "title": "Title", "groups": "NumGroups", "projects": "NumProjects", "dept": "Department"
      };
    
    private effortsSubject = new BehaviorSubject<Effort[]>([]);
    efforts$ = this.effortsSubject.asObservable();

    private sourceUpdatedSubject = new BehaviorSubject<boolean>(false);
    sourceUpdated$ = this.sourceUpdatedSubject.asObservable();

    constructor(private http: HttpClient) {
        super();
    }

    fetchEfforts() {
        this.http.get<Effort[]>(environment.apiUrl + 'efforts/search?filter.pageSize=10000').subscribe(efforts => {
            this.effortsSubject.next(efforts);
        });
    }

    addEffort(newEffort: Effort) {
        this.http.post(environment.apiUrl + 'efforts', newEffort).subscribe(() => {
            this.sourceUpdatedSubject.next(true);
        });
    }

    addEffortGroups(effort: Effort, groups: Group[]) {
        let groupEfforts = groups.map(g => <GroupEffort>{ groupId: g.id, effortId: effort.id });
        this.http.post(environment.apiUrl + `efforts/${effort.id}/groups`, groupEfforts).subscribe(() => {
            this.sourceUpdatedSubject.next(true);
        });
    }

    updateEffort(updatedEffort: Effort) {
        this.http.put(environment.apiUrl + `efforts/${updatedEffort.id}`, updatedEffort).subscribe(() => {
            this.sourceUpdatedSubject.next(true);
        });
    }

    deleteEffort(effort: Effort) {
        this.http.delete(environment.apiUrl + `efforts/${effort.id}`).subscribe(() => {
            this.sourceUpdatedSubject.next(true);
        });
    }

    removeGroupEffort(groupId: number, effortId: number) {
        this.http.delete(environment.apiUrl + `efforts/${effortId}/groups/${groupId}`).subscribe(() => {
            this.sourceUpdatedSubject.next(true);
        });
    }

    filterEfforts(filters: FilterChip[], pageSize: number = 10, pageIndex: number = 0, sortField: string | undefined = undefined, sortDirection: string | undefined = undefined) {
        return this.sourceUpdated$.pipe(
            switchMap(() => {
                const keywords = filters.filter(c => c.chip == null).reduce((v, c) => v + ' ' + c.value, '');
                const propertyValues = filters.reduce((v, c) => { if (c.chip != null) v[c.chip] = c.value; return v; }, <{ [key: string]: string }>{});
                const filter = <any>{ keywords, propertyValues };
                if (pageSize) filter['pageSize'] = ''+pageSize
                if (pageIndex) filter['page'] = ''+pageIndex;
                if (sortField) filter['SortSpecifications[0].Property'] = this.sortProperties[sortField];
                if (sortDirection) filter['SortSpecifications[0].Direction'] = sortDirection == "asc" ? "Ascending" : "Descending";
                const filterQuery = this.serialize(filter);
                console.log(filterQuery, filter);
                return this.http.get<Effort[]>(environment.apiUrl + 'efforts/search?' + filterQuery, { observe: 'response' })
                    .pipe(
                        map(response => {
                            const paging = response.headers.get('Content-Range');
                            const total = parseInt(paging?.split("/")[1] ?? '0');
                            const efforts = response.body ?? [];
                            return { total, efforts };
                        })
                    );
            })
        );
    }
}